import { compact, isEmpty } from 'modules/object_utils';

function selectedAxisClass(selector) {
  const link = document.querySelector(`${selector} a.selected`);
  return link?.classList[0] || '';
}

export function createChartHelper({ dataSets, getChart, setChart }) {
  const helper = {
    categories: undefined,
    data: undefined,

    setup(categories, data) {
      this.categories = categories;
      this.data = data;
    },

    parse_dataset_id(classname) {
      const match = (classname || '0').match(/[0-9]/g);
      return parseInt((match || ['0']).join(''), 10);
    },

    get_settings() {
      return {
        title: 'default title',
        x: selectedAxisClass('#chart_x'),
        y: selectedAxisClass('#chart_y'),
        type: selectedAxisClass('.chart_type')
      };
    },

    get_series_name(index) {
      const name = dataSets[index] ? dataSets[index].dataset_name : '';
      return isEmpty(name) ? `Data set ${index}` : name;
    },

    get_series_chart_type(index) {
      return helper.get_settings().type.replace('_stack', '');
    },

    cumulative() {
      return document.getElementById('cumulative_data')?.checked || false;
    },

    cumulativize(ds) {
      Object.keys(ds).forEach((key) => {
        let cnt = 0;
        const indices = Object.keys(ds[key]);
        Object.entries(ds[key]).forEach(([index, value]) => {
          if (cnt > 0) {
            ds[key][index] = value + ds[key][indices[cnt - 1]];
          }
          cnt += 1;
        });
      });
      return ds;
    },

    build_chart_data() {
      const hashedCategories = {};
      this.categories.forEach((value) => {
        hashedCategories[value] = 0;
      });

      const datasetByCapacity = [];
      const datasetByUnits = [];
      const datasetByPlantCount = [];
      const lastIndex = Object.keys(this.data).pop();

      for (let cnt = 0; cnt < lastIndex; cnt += 1) {
        if (this.data[cnt] === undefined) {
          datasetByCapacity[cnt] = { ...hashedCategories };
          datasetByUnits[cnt] = { ...hashedCategories };
          datasetByPlantCount[cnt] = { ...hashedCategories };
        }
      }

      Object.entries(this.data).forEach(([index, arrayOfObjects]) => {
        datasetByCapacity[index] = { ...hashedCategories };
        datasetByUnits[index] = { ...hashedCategories };
        datasetByPlantCount[index] = { ...hashedCategories };

        arrayOfObjects.forEach((entry) => {
          if (datasetByCapacity[index][entry.category] !== undefined) {
            datasetByCapacity[index][entry.category] = entry.capacity;
          }
          if (datasetByUnits[index][entry.category] !== undefined) {
            datasetByUnits[index][entry.category] = entry.units;
          }
          if (datasetByPlantCount[index][entry.category] !== undefined) {
            datasetByPlantCount[index][entry.category] = entry.plants;
          }
        });
      });

      if (helper.cumulative()) {
        helper.cumulativize(datasetByCapacity);
        helper.cumulativize(datasetByPlantCount);
        helper.cumulativize(datasetByUnits);
      }

      const mapDataset = (collection) =>
        collection.map((object, index) => ({
          type: helper.get_series_chart_type(index),
          data: Object.values(object),
          name: helper.get_series_name(index)
        }));

      return {
        by_capacity: mapDataset(datasetByCapacity),
        by_units: mapDataset(datasetByUnits),
        by_plant_count: mapDataset(datasetByPlantCount)
      };
    },

    build_options() {
      const chartData = this.build_chart_data();
      let seriesData = chartData.by_capacity;
      const settings = this.get_settings();
      const title = this.build_chart_title(settings);

      switch (settings.y) {
        case 'capacity':
          seriesData = chartData.by_capacity;
          break;
        case 'total_plants':
          seriesData = chartData.by_plant_count;
          break;
        case 'total_units':
          seriesData = chartData.by_units;
          break;
        default:
          break;
      }

      seriesData = seriesData.filter((series) => !isEmpty(compact(series.data)));

      if (settings.type === 'pie') {
        seriesData = seriesData.map((object) => ({
          ...object,
          data: object.data.map((value, indx) => [`${helper.categories[indx]}`, value])
        }));
      }

      const options = {
        legend: {
          backgroundColor: '#FFFFFF',
          floating: true,
          verticalAlign: 'top',
          y: 25,
          style: { fontSize: '9px' }
        },
        xAxis: {
          tickmarkPlacement: 'on',
          title: {
            text: title.x_axis_title,
            margin: 10,
            style: {
              color: '#444444',
              fontWeight: 'normal',
              fontSize: '9px',
              fontStyle: 'italic'
            }
          },
          categories: this.categories,
          labels: {
            rotation: 50,
            overflow: 'justify',
            style: {
              color: '#000000',
              fontFamily: 'Verdana',
              fontSize: '8px'
            }
          }
        },
        yAxis: {
          min: 0,
          title: {
            text: title.y_axis_title,
            margin: 10,
            style: {
              color: '#444444',
              fontWeight: 'normal',
              fontSize: '9px',
              fontStyle: 'italic'
            }
          },
          labels: {
            step: 2,
            style: { color: '#555555', fontSize: '9px' }
          },
          gridLineColor: '#c6c6c6'
        },
        title: {
          useHTML: true,
          text: title.title,
          style: { fontWeight: 'bold', fontSize: '16px', color: '#555555' }
        },
        credits: {
          text: 'Source : DesalData.com',
          href: 'http://desaldata.com',
          style: { color: '#CD4126' }
        },
        colors: [
          '#cd4126', '#5d6b62', '#BFB28A', '#26742b', '#F9AE85', '#A4C9EF',
          '#dede4c', '#be1d7d', '#ef9d00', '#a3c536', '#ffea4c', '#b58018',
          '#d2e1f0', '#09AE85', '#13A5F9', '#5A8275', '#6B6B4E', '#649419',
          '#cccccc', '#6d261b'
        ],
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            borderWidth: 1,
            borderColor: '#FFFFFF'
          },
          series: {
            pointPadding: 0.1,
            groupPadding: 0.05,
            borderWidth: settings.type === 'pie' ? 1 : 0,
            shadow: false
          }
        },
        series: seriesData,
        loading: {
          labelStyle: {
            fontWeight: 'bold',
            position: 'relative',
            top: '50%',
            color: '#cd4126'
          }
        }
      };

      options.subtitle = {};
      if (this.cumulative()) {
        options.subtitle = {
          html: true,
          style: { fontWeight: 'bold', fontSize: '13px', color: '#555555' },
          text: '(Cumulative)',
          floating: false,
          y: 33,
          align: 'center'
        };
        options.legend.y = 40;
      }

      if (settings.type === 'column_stack') {
        options.plotOptions.column = {
          stacking: 'normal',
          dataLabels: { enabled: false }
        };
      }

      if (settings.type === 'pie') {
        options.series = [options.series[0]];
      }

      if (this.categories.length > 21) {
        options.xAxis.labels.step = Math.floor((this.categories.length / 10) / 2);
      }

      return options;
    },

    redraw() {
      const chart = getChart();
      chart?.destroy();
      setChart(Highcharts.chart('container', this.build_options()));
    },

    build_chart_title(s) {
      const titleInput = document.getElementById('chart_title')?.value || '';

      const xTitle = {
        technology: ['by technology', 'technologies'],
        award_date: ['', 'contract year'],
        online_date: ['', 'online year'],
        rawwatertype: ['by raw water type', 'raw water types'],
        customertype: ['by customer type', 'customer types']
      };

      const yTitle = {
        capacity: ['Total capacity', 'capacity (m<sup>3</sup>/d)'],
        total_plants: ['Total plants', 'number of plants'],
        total_units: ['Total plant units', 'number of plant units']
      };

      const o = { title: [], x_axis_title: '', y_axis_title: '' };

      if (s.x === 'award_date' || s.x === 'online_date') {
        o.title.push('Annual new');
        o.title.push(s.x === 'award_date' ? 'contracted' : 'online');
        o.title.push(yTitle[s.y][0].replace('Total ', ''));
      } else {
        o.title.push(yTitle[s.y][0]);
        o.title.push(xTitle[s.x][0]);
      }

      o.x_axis_title = xTitle[s.x][1];
      o.y_axis_title = yTitle[s.y][1];
      o.title.push(s.y === 'capacity' ? '(m<sup>3</sup>/d)' : '');
      o.title = o.title.join(' ');
      o.title = titleInput.trim() !== '' ? titleInput : o.title;

      return o;
    },

    setup_help_pane() {
      const helpDialog = document.getElementById('help_analysis_div');
      if (!helpDialog) return;

      const steps = {};
      helpDialog.querySelectorAll('#analysis-steps .analysis-step').forEach((el) => {
        const stepNumber = el.className.split(' ')[1].replace(/[^0-9]/gi, '');
        steps[stepNumber] = el.querySelector('h5')?.textContent?.trim() || '';
      });

      const showStep = (stepNumber) => {
        const footer = document.querySelector('.help_analysis_footer');
        const pages = [];

        if (Number(stepNumber) === 1) {
          helpDialog.querySelector('.help_analysis_text')?.style.setProperty('display', '');
          pages.push(`<a class="left" href="#" data-target-step="5"><i class="fa fa-chevron-left"></i> ${steps[5]}</a>`);
          pages.push(`<a class="right" href="#" data-target-step="2">${steps[2]} <i class="fa fa-chevron-right"></i></a>`);
        } else {
          helpDialog.querySelector('.help_analysis_text')?.style.setProperty('display', 'none');
          pages.push(`<a class="left" href="#" data-target-step="${stepNumber - 1}"><i class="fa fa-chevron-left"></i> ${steps[stepNumber - 1]}</a>`);
          if (steps[stepNumber + 1] !== undefined) {
            pages.push(`<a class="right" href="#" data-target-step="${stepNumber + 1}">${steps[stepNumber + 1]} <i class="fa fa-chevron-right"></i></a>`);
          } else {
            pages.push(`<a class="right" href="#" data-target-step="1">${steps[1]} <i class="fa fa-chevron-right"></i></a>`);
          }
        }

        if (footer) footer.innerHTML = pages.join('');

        helpDialog.querySelectorAll('#analysis-steps .analysis-step').forEach((step) => {
          step.style.display = 'none';
        });
        const active = helpDialog.querySelector(`#analysis-steps .analysis-step-${stepNumber}`);
        if (active) active.style.display = '';

        helpDialog.hidden = false;
        helpDialog.style.display = '';
      };

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('.ui-icon-help, .help_analysis_footer a');
        if (!trigger) return;

        event.preventDefault();
        event.stopPropagation();

        const stepNumber = trigger.dataset.targetStep;
        if (stepNumber) showStep(stepNumber);
      });

      document.addEventListener('keyup', (event) => {
        if (helpDialog.hidden) return;
        if (event.key === 'ArrowLeft') {
          document.querySelector('.help_analysis_footer a.left')?.click();
        } else if (event.key === 'ArrowRight') {
          document.querySelector('.help_analysis_footer a.right')?.click();
        }
      });
    }
  };

  return helper;
}
